import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { ReactiveFormsModule, FormControl, Validators } from '@angular/forms';
import { DestroyController } from '@celerofinancas/class-utils';
import { CdsSnackbarService } from '@celerofinancas/design-system';
import { UiDirectivesModule } from '@celerofinancas/ui-directives';
import { ModalRef } from '@celerofinancas/ui-modals';
import { finalize, take, takeUntil } from 'rxjs';
import {
  CREDIT_CARDS_TABLE_DATA_SOURCE,
  CreditCardsTableDataSource,
} from '../credit-cards-data-source.token';

/**
 * Data passed to the modal via ModalService.
 */
export interface EditContractedProductModalData {
  id: string;
  name: string;
  showHeader?: boolean;
}

/**
 * Data returned by the modal on close.
 * name is present on success so the caller can update the header without re-fetching.
 */
export interface EditContractedProductModalResult {
  success: boolean;
  name?: string;
}

/**
 * Local modal component for editing the contracted product nickname.
 * Uses the Figma layout and delegates the HTTP call to MultipleAccountsRelationshipService,
 * which is provided as CaixaMultipleAccountsAdapterService at root level.
 */
@Component({
  selector: 'caixa-webcomponent-edit-contracted-product-modal',
  standalone: true,
  templateUrl: './edit-contracted-product-modal.component.html',
  styleUrl: './edit-contracted-product-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    UiDirectivesModule,
  ],
})
export class EditContractedProductModalComponent extends DestroyController {
  /**
   * Max length for the nickname input.
   */
  public readonly maxLength = 50;

  /**
   * Loading state while the update request is in flight.
   */
  public readonly loading = signal(false);

  /**
   * Modal reference injected by ModalService.
   */
  public readonly modalRef = inject(ModalRef) as ModalRef<
    EditContractedProductModalResult,
    EditContractedProductModalData
  >;

  /**
   * Service responsible for updating the contracted product name.
   * Uses the same data source configured for the credit cards table.
   */
  private readonly dataSource = inject<CreditCardsTableDataSource>(
    CREDIT_CARDS_TABLE_DATA_SOURCE,
  );

  /**
   * Snackbar service for error notifications.
   */
  private readonly snackbar = inject(CdsSnackbarService);

  /**
   * Form control for the nickname input, initialized with the current product name.
   */
  public readonly contractedProductName = new FormControl(this.modalRef.data?.name ?? '', [
    Validators.maxLength(this.maxLength),
  ]);

  /**
   * Closes the modal without saving.
   */
  public closeModal(): void {
    this.modalRef.close({ success: false });
  }

  /**
   * Submits the nickname update request.
   */
  public onSubmit(): void {
    if (this.loading() || !this.contractedProductName.value?.length) {
      return;
    }

    this.loading.set(true);

    this.dataSource
      .updateContractedProduct({
        id: this.modalRef.data.id,
        name: this.contractedProductName.value,
      })
      .pipe(
        take(1),
        finalize(() => this.loading.set(false)),
        takeUntil(this.destroyController$),
      )
      .subscribe({
        next: () => this.modalRef.close({ success: true, name: this.contractedProductName.value ?? undefined }),
        error: () =>
          this.snackbar.alert(
            'error',
            'Não foi possível editar o produto contratado. Tente novamente mais tarde.',
          ),
      });
  }
}
